/**
 * The 35 power-ups (spec 02 §7.5; `POWERUPS` in src/sim/items/powerups.ts): shoe, staffs, candies,
 * crosses, lamps, rings, clock, potions, crystal balls, umbrellas, heart, boxes, bomb, bell, doors, the
 * lightning potion. Each about 1.4 tiles across (the doors about 2x2), built on its base at y = 0 and
 * facing the camera, in the bright, cartoony colours of the arcade's item sprites (the owner's choice:
 * "follow arcade"). The game draws them at scale 1.35.
 */

import { MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';
import { type Builder, candy, cross, ring, shoe, staff } from './charms';
import { door } from './doors';
import { bomb, chest, clock, powerHeart, umbrella } from './things';
import { bell, crystalBall, lamp, lightningPotion, potion } from './vessels';

/** Model builders by `POWERUPS` name. */
const BUILDERS: Record<string, Builder> = {
  shoe,
  whiteStaff: staff('puWhite', 'puGold', 'puWhite.0'),
  redStaff: staff('puRed', 'puGold'),
  yellowStaff: staff('puYellow', 'puRed'),
  blueStaff: staff('puBlue', 'puGold'),
  yellowCandy: candy('puYellow'),
  purpleCandy: candy('puPurple'),
  blueCandy: candy('puBlue'),
  redCross: cross('puRed'),
  yellowCross: cross('puYellow'),
  blueCross: cross('puBlue'),
  redLamp: lamp('puRed', 'puGold'),
  yellowLamp: lamp('puYellow', 'puOrange'),
  purpleLamp: lamp('puPurple', 'puGold'),
  greenLamp: lamp('puGreen', 'puGold'),
  purpleRing: ring('puPurple'),
  redRing: ring('puRed'),
  blueRing: ring('puBlue'),
  clock,
  greenPotion: potion('puGreen'),
  purplePotion: potion('puPurple'),
  yellowPotion: potion('puYellow'),
  greenCrystalBall: crystalBall('puGreen'),
  purpleCrystalBall: crystalBall('puPurple'),
  blueUmbrella: umbrella('puBlue'),
  purpleUmbrella: umbrella('puPurple'),
  powerHeart,
  yellowBox: chest('puYellow', 'puWood'),
  blueBox: chest('puBlue', 'puGold'),
  whiteBox: chest('puWhite', 'puGold'),
  bomb,
  bell,
  redDoor: door('puRed'),
  whiteDoor: door('puWhite'),
  lightningPotion,
};

/** The model of the power-up whose `POWERUPS` name is `name`; a plain gold token for an unknown name. */
export function powerupModel(name: string, p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  const build = BUILDERS[name];
  if (build) build(mb, p);
  else {
    const coin = place(0, 0.45, -0.1, 0, 1, Math.PI / 2);
    mb.cylinder(0.45, 0.45, 0.2, 16, p.color('puGold.1'), coin, p.color('puGold.2'));
  }
  return mb;
}
